import React, { FC, useState } from 'react';
import { useRouter } from 'next/router';
import { useForm, FormProvider } from 'react-hook-form';
import { useToast } from '@common/toast';
import Certification from '@components/acca/result/save/certification';
import CertificationFail from '@components/acca/result/save/certificationFail';
import SavePage from '@components/acca/result/save/savePage';
import SaveCompletePage from '@components/acca/result/save/saveCompletePage';
import OverValidPeriod from '@components/acca/result/save/errorPage/OverValidPeriod';
import AlreadySavedResult from '@components/acca/result/save/errorPage/AlreadySavedResult';
import { getAccessToken } from '@repository/AccessToken';
import SaveModals, { ModalTYpe } from '@components/acca/result/save/modals';
import { useCheckIdentity } from '@repository/identity/useIdentity';
import { useAvailableCopyApply, useCopyApply } from '@repository/acc/useAcca';
import WrongKeyResult from '@components/acca/result/save/errorPage/WrongKeyResult';
import { useUserInfo } from '@repository/auth/useUserInfo';
import LoginModal from '@components/loginModal';
import { ApplyCopyTaskDto, ApplyCopyTaskListRs } from '@domain/rs/acca/save/ApplyCopyTaskListRs';
import { NCC_SUPPORT_EMAIL } from '@domain/constant/contact';
import classnames from 'classnames/bind';
import styles from './resultSave.module.scss';
const cx = classnames.bind(styles);

export type ContentType =
  | 'VERIFICATION'
  | 'VERIFICATION_FAIL'
  | 'ACCA_SAVE'
  | 'ACCA_SAVE_SUCCESS'
  | 'ERROR_VALIDITY_PERIOD'
  | 'ERROR_ALREADY_SAVE'
  | 'ERROR_WRONG_KEY'
  | null;

interface ACCAResultSaveProps {
  isLoading: boolean;
  setModalState: (modalTYpe: ModalTYpe) => void;
  contentState: ContentType;
  setContentState: (contentType: ContentType) => void;
}

const ACCAResultSave: FC<ACCAResultSaveProps> = ({ isLoading, setModalState, contentState, setContentState }) => {
  const isLogin = !!getAccessToken();
  const { data: isIdentity, isFetched } = useCheckIdentity(isLogin);

  const isVisibleContents = (contentType: ContentType) => contentType === contentState;

  if (!isFetched) return null;
  //본인 인증 안되었을 경우
  if (!isIdentity)
    return <Certification onLoading={() => setModalState('LOADING')} onCloseModal={() => setModalState(null)} />;

  if (isVisibleContents('VERIFICATION_FAIL')) return <CertificationFail />;
  if (isVisibleContents('ACCA_SAVE_SUCCESS')) return <SaveCompletePage />;
  if (isVisibleContents('ERROR_VALIDITY_PERIOD')) return <OverValidPeriod />;
  if (isVisibleContents('ERROR_ALREADY_SAVE')) return <AlreadySavedResult />;
  if (isVisibleContents('ERROR_WRONG_KEY')) return <WrongKeyResult />;

  return <SavePage isLoading={isLoading} />;
};

export const ACCAResultSaveContext = React.createContext({
  tasks: [] as ApplyCopyTaskDto[],
  setTasks: (tasks: ApplyCopyTaskDto[]) => {
    console.log(tasks);
  },
});

interface IFormDataVO {
  name: string;
}

const ACCAResultSaveTemplate = () => {
  const { data: userInfo } = useUserInfo();
  const isLogin = !!userInfo?.id;
  const Toast = useToast();

  const { query, isReady } = useRouter();
  const accaKey = decodeURIComponent(query?.key?.toString() ?? '');

  const methods = useForm<IFormDataVO>({
    defaultValues: { name: '' },
  });

  const [modalState, setModalState] = useState<ModalTYpe>(null);
  const [contentState, setContentState] = useState<ContentType>(null);
  //추가 응시 항목 리스트
  const [tasks, setTasks] = useState<ApplyCopyTaskDto[]>([]);

  //복사 하기 API 호출
  const { mutate: copyApplyMutate, isLoading } = useCopyApply(
    (data: ApplyCopyTaskListRs) => {
      if (data.tasks.length) {
        setTasks(data.tasks);
        setModalState('ADDITIONAL_TASKS');
        return;
      }

      setModalState('SAVE_SUCCESS');
    },
    (err) => {
      switch (err?.response?.data?.errorCode) {
        case 'B901':
          setContentState('VERIFICATION_FAIL');
          break;
        case 'B903':
          setContentState('ERROR_WRONG_KEY');
          break;
        case 'B904':
          setContentState('ERROR_VALIDITY_PERIOD');
          break;
        case 'B905':
          setContentState('ERROR_ALREADY_SAVE');
          break;
        case 'B902':
        default:
          setModalState('SAVE_FAIL');
          return;
      }

      setModalState(null);
    },
  );

  //유효한 key인지 확인
  const { isFetched } = useAvailableCopyApply(isReady, accaKey, (err) => {
    switch (err?.response?.data?.errorCode) {
      case 'B901':
        setContentState('VERIFICATION_FAIL');
        return;
      case 'B903':
        setContentState('ERROR_WRONG_KEY');
        return;
      case 'B904':
        setContentState('ERROR_VALIDITY_PERIOD');
        return;
      case 'B905':
        setContentState('ERROR_ALREADY_SAVE');
        return;
      case 'B902':
      default:
        Toast({
          type: 'error',
          iconType: 'info',
          content: (
            <>
              알 수 없는 오류가 발생했습니다.
              <br />
              {NCC_SUPPORT_EMAIL}으로
              <br />
              문의해 주세요.
            </>
          ),
        });
        return;
    }
  });

  const onSubmit = (data: IFormDataVO) => {
    if (isLoading) return;
    const name = data.name || '역검 응시 결과';
    copyApplyMutate({ key: accaKey, name });
    setModalState('SAVE_LOADING');
  };

  if (!isFetched) return null;

  return (
    <>
      <ACCAResultSaveContext.Provider value={{ tasks, setTasks }}>
        <FormProvider {...methods}>
          <form autoComplete={'off'} onSubmit={methods.handleSubmit(onSubmit)}>
            <div className={cx('wrap')}>
              <ACCAResultSave
                isLoading={isLoading}
                contentState={contentState}
                setContentState={setContentState}
                setModalState={setModalState}
              />
              <SaveModals modalState={modalState} setModalState={setModalState} setContentState={setContentState} />
            </div>
          </form>
        </FormProvider>
      </ACCAResultSaveContext.Provider>
      <LoginModal subTitle="채용정보 · 역량검사 · 매칭까지" title="취업기회 지금 바로 잡다!" visible={!isLogin} />
    </>
  );
};

export default ACCAResultSaveTemplate;
